import { API_ORIGIN } from '../config';
/**
 * Payment Link Handler Utility
 * 
 * Checks if a payment link exists for a form and redirects the user if required.
 * Can be used by any form after successful submission.
 */

/**
 * Check and redirect to payment link if exists and enabled
 * 
 * @param {string} formName - The name of the application form (e.g., "Għannejja Ġodda", "RockNMalta", "DanceOn")
 * @returns {Promise<boolean>} - Returns true if redirect happened, false otherwise
 * 
 * @example
 * // After successful form submission:
 * const redirected = await checkAndRedirectToPayment('Għannejja Ġodda');
 * if (redirected) {
 *   console.log('User redirected to payment');
 * } else {
 *   console.log('No payment required');
 * }
 */
export const checkAndRedirectToPayment = async (formName) => {
  try {
    const response = await fetch(
      `${API_ORIGIN}/api/payment-links/form/${encodeURIComponent(formName)}`
    );
    const data = await response.json();

    if (response.ok && data.success && data.paymentLink) {
      window.location.href = data.paymentLink;
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

/**
 * Get payment link information without redirecting
 * 
 * @param {string} formName - The name of the application form
 * @returns {Promise<Object|null>} - Returns payment link data or null
 * 
 * @example
 * const paymentInfo = await getPaymentLinkInfo('DanceOn');
 * if (paymentInfo && paymentInfo.paymentLink) {
 *   console.log('Payment URL:', paymentInfo.paymentLink);
 * }
 */
export const getPaymentLinkInfo = async (formName) => {
  try {
    const response = await fetch(
      `${API_ORIGIN}/api/payment-links/form/${encodeURIComponent(formName)}`
    );
    const data = await response.json();

    if (response.ok && data.success && data.paymentLink) {
      return data.data;
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Check if payment is required for a form (without redirecting)
 * 
 * @param {string} formName - The name of the application form
 * @returns {Promise<boolean>} - Returns true if payment is required
 * 
 * @example
 * const isPaymentRequired = await isPaymentRequiredForForm('RockNMalta');
 * if (isPaymentRequired) {
 *   // Show payment message to user
 * }
 */
export const isPaymentRequiredForForm = async (formName) => {
  const paymentInfo = await getPaymentLinkInfo(formName);
  return paymentInfo !== null && paymentInfo.hasLink === 1;
};

/**
 * Manual redirect to payment link
 * Useful for showing a message before redirecting or for delayed redirects
 * 
 * @param {string} formName - The name of the application form
 * @param {number} delay - Optional delay in milliseconds before redirect
 * @returns {Promise<boolean>} - Returns true if redirect will happen
 * 
 * @example
 * // Redirect after 3 seconds
 * await redirectToPayment('Għannejja Ġodda', 3000);
 */
export const redirectToPayment = async (formName, delay = 0) => {
  const paymentInfo = await getPaymentLinkInfo(formName);
  
  if (paymentInfo && paymentInfo.paymentLink) {
    if (delay > 0) {
      setTimeout(() => {
        window.location.href = paymentInfo.paymentLink;
      }, delay);
    } else {
      window.location.href = paymentInfo.paymentLink;
    }
    return true;
  }
  return false;
};

export default {
  checkAndRedirectToPayment,
  getPaymentLinkInfo,
  isPaymentRequiredForForm,
  redirectToPayment
};
